import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Copy, Globe, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const Blog = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const question = location.state?.question || '';
  const userId = location.state?.user_id || user?.id;

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // Auto-generate if question is provided
    if (question && userId && !result && !error) {
      generateBlog();
    }
  }, [user, question, userId]);

  const generateBlog = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/generate/blog`, {
        question: question,
        user_id: userId,
        target_length: 1600
      });
      setResult(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      if (errorMsg.includes('Profile not found')) {
        setError('Please complete your profile first to generate blog posts.');
        setTimeout(() => navigate('/profile'), 3000);
      } else if (errorMsg.includes('502') || errorMsg.includes('APIError')) {
        setError('OpenAI API is temporarily unavailable (Error 502). Please try again in a few minutes.');
      } else {
        setError('Error generating blog: ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-page">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/keyword-research')} variant="ghost" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <Globe className="w-6 h-6 text-orange-600" />
              <span className="text-xl font-bold text-gray-900">Blog Generator</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Question Display */}
        {question && (
          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white mb-8 shadow-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">❓</span>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-orange-100 mb-2">ANSWERING THIS QUESTION:</h2>
                <p className="text-2xl font-bold">{question}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-200 text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-orange-600" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Generating Your Blog Post...</h3>
            <p className="text-gray-600">This will take about 30-60 seconds. We're creating a comprehensive, SEO-optimized blog post tailored to your business.</p>
            <div className="mt-8 flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Analyzing your business profile</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Researching the question</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                <span>Writing comprehensive content...</span>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="bg-red-50 rounded-2xl p-12 shadow-lg border-2 border-red-200 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">⚠️</span>
            </div>
            <h3 className="text-2xl font-bold text-red-900 mb-2">Generation Failed</h3>
            <p className="text-red-700 mb-6">{error}</p>
            <Button 
              onClick={() => {
                setError(null);
                generateBlog();
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        )}

        {/* Results */}
        {!loading && result && (
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200" data-testid="blog-results">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Your Blog Post is Ready! 🎉</h2>
                <p className="text-gray-600 mt-2">{result.word_count} words of SEO-optimized content</p>
              </div>
              <Button 
                onClick={generateBlog}
                variant="outline"
                className="border-orange-600 text-orange-600 hover:bg-orange-50"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
            </div>

            <div className="space-y-8">
              {/* Post Title */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold">Post Title</Label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.title)} data-testid="copy-title">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 border-2 border-orange-200">
                  <p className="font-bold text-2xl text-gray-900">{result.title}</p>
                </div>
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold">Content ({result.word_count} words)</Label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.content)} data-testid="copy-content">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All
                  </Button>
                </div>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 max-h-[600px] overflow-y-auto">
                  <div className="whitespace-pre-wrap prose prose-lg max-w-none">{result.content}</div>
                </div>
              </div>

              {/* CTA Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold">Call-to-Action</Label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(result.cta_section)} data-testid="copy-cta">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                  <p className="text-gray-900">{result.cta_section}</p>
                </div>
              </div>

              {/* SEO Metadata */}
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">SEO Metadata</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm text-gray-600">Meta Title ({result.meta_title?.length || 0}/65 characters)</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{result.meta_title}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">URL Slug</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-mono text-gray-900">{result.slug}</p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sm text-gray-600">Meta Description ({result.meta_description?.length || 0}/240 characters)</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-900">{result.meta_description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Word Count Warning */}
              {result.word_count < 1500 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6">
                  <p className="text-amber-900 font-semibold">⚠️ Word count is below recommended 1,500 words for SEO</p>
                  <p className="text-sm text-amber-800 mt-1">Consider clicking "Regenerate" to get more comprehensive content.</p>
                </div>
              )}

              {/* Next Steps */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-8 border-2 border-orange-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">🚀 Next Steps</h3>
                <div className="space-y-3 text-gray-700">
                  <p>✓ Copy the content to your blog/CMS</p>
                  <p>✓ Add your own images or screenshots</p>
                  <p>✓ Review and add any business-specific examples</p>
                  <p>✓ Publish and promote on social media</p>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button 
                    onClick={() => navigate('/keyword-research')}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Find More Questions →
                  </Button>
                  <Button 
                    onClick={() => copyToClipboard(`${result.title}\n\n${result.content}\n\n${result.cta_section}`)}
                    variant="outline"
                    className="border-orange-600 text-orange-600"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Complete Blog
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Question State */}
        {!loading && !result && !question && (
          <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-200 text-center">
            <Globe className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Question Selected</h3>
            <p className="text-gray-600 mb-6">Please go to Keyword Research to select a question to answer</p>
            <Button 
              onClick={() => navigate('/keyword-research')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Go to Keyword Research →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;